import { CheckCircle2, Sparkles } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Field, Steps, NextStep, Callout, RoleBadge, pathFor,
  Diagram, MockField, MockButton, MockTable,
} from "../shared";

export default function FormsApproveCloseSection() {
  return (
    <div data-testid="guide-forms-approve-close">
      <PageHeading
        icon={CheckCircle2}
        title="Approve & Close (from the MD Queue)"
        description="The single button the SDF MD uses to clear items from the Review Queue. It closes SDF MD items outright, and for items heading higher up it moves them to Awaiting Dispatch — recording sign-off for you automatically."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="md_admin" />
        <span className="text-xs text-white/40">Restricted to the actual SDF MD principal or a Super Admin. MD Admin (read-only) cannot do this.</span>
      </div>

      <Callout variant="info" title="In plain words">
        This is the MD's <span className="text-white">"yes, approved"</span> button. Tick the items you're happy with, write one note, and the system finishes each one the right way — closing it or sending it on to be dispatched.
      </Callout>

      <Callout variant="info" title="Why one button now (v2.5.0)">
        <span className="inline-flex items-center gap-1"><Sparkles className="w-3 h-3 text-amber-300" /></span> v2.5.0 merged the old "Approve &amp; Close" and "Sign-off &amp; Close" buttons into one. The system works out whether sign-off is needed and records it automatically with the closure. See <Link href={pathFor("upward-dispatch")} className="text-cyan-300 hover:underline">Upward Dispatch</Link>.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-sm text-white/65 space-y-1.5 ml-2">
          <li>When you're the SDF MD clearing a batch of reviewed items in one go.</li>
          <li>If an item needs more work instead, use <Link href={pathFor("forms/return")} className="text-cyan-300 hover:underline">Return for Amendments</Link> — see the warning below.</li>
        </ul>
      </Section>

      <Section title="What the screen looks like">
        <Diagram
          frameLabel="MD Meetings → Review Queue → Approve & Close"
          legend={[
            <>Tick the <span className="text-white">checkbox</span> on each row you want to action.</>,
            <>Click the green <span className="text-white">Approve &amp; Close</span> button above the table.</>,
            <>In the dialog, write one <span className="text-white">closing note</span> that covers the whole batch.</>,
          ]}
        >
          <MockButton label="Approve & Close" marker={2} />
          <MockTable
            columns={["✓", "Item", "Lane", "Status"]}
            rows={[
              [<span className="text-cyan-300">☑</span>, "Board resolution 14", "Board / ExCom", "With SDF MD Review"],
              [<span className="text-cyan-300">☑</span>, "Q3 portfolio pack", "SDF MD", "With SDF MD Review"],
            ]}
          />
          <MockField label="Closing / approval note" placeholder="Reviewed and approved at 10 Apr MD meeting." marker={3} />
        </Diagram>
        <p className="text-2xs text-white/40">The checkboxes on the left of each row are marker <span className="text-cyan-300">①</span>.</p>
      </Section>

      <Section title="Step-by-step">
        <Steps
          steps={[
            <>Open <span className="text-white">MD Meetings</span> from the header and stay on the <span className="text-purple-300">Review Queue</span> tab (the default). Tick the rows you want to action — one or many.</>,
            <>Click the green <span className="text-white">Approve &amp; Close</span> button at the top of the table.</>,
            <>In the dialog, write one <span className="text-white">closing note</span>, check the count, and confirm.</>,
          ]}
        />
      </Section>

      <Section title="The boxes in the dialog">
        <Field name="Closing / approval note" required="required">
          <p>One note covers the whole batch. Describe why you're approving — it's recorded against every selected item.</p>
          <p className="text-white/40">Examples: "Reviewed and approved at 10 Apr MD meeting.", "All four items aligned with strategy — approved for upward submission."</p>
        </Field>

        <Field name="Confirm count" required="auto">
          <p>The dialog reminds you how many items you're about to action so you can double-check before confirming.</p>
        </Field>
      </Section>

      <NextStep>
        <p>For each ticked item the system:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Records sign-off automatically if the item needed it (event <span className="text-white">SIGNOFF_GRANTED</span>).</li>
          <li>If it's in the <span className="text-white">SDF MD</span> lane, closes it (event <span className="text-white">STATUS_CHANGE</span> → CLOSED with your shared note).</li>
          <li>If it's in an <span className="text-white">above-MD</span> lane (EDGE Chairman, EDGE MD Office, Board / ExCom), sets it to <span className="text-yellow-300">Awaiting Dispatch</span> and notifies the PMO and Board Secretary.</li>
          <li>Sends the usual email notifications to the assignee / owner.</li>
        </ul>
      </NextStep>

      <Section title="Doing just one item">
        <p className="text-sm text-white/65 leading-relaxed">
          You can also action a single item without ticking the checkbox by opening it from the queue and using its detail-page controls. Bulk Approve &amp; Close is just faster when you're clearing a meeting's worth of items at once.
        </p>
      </Section>

      <Callout variant="warning" title="If you need rework instead">
        Don't approve. Use <Link href={pathFor("forms/return")} className="text-cyan-300 hover:underline">Return for Amendments</Link> on the row instead — that sends the item back to the original assignee with your reason.
      </Callout>
    </div>
  );
}
